import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { NEUTRAL, SHADOW, SURFACE } from "../../../theme/tokens";

type Props = {
	/** The wide content (a table, a chart, a chip row) that should scroll rather than stretch its container. */
	children: ReactNode;
	/**
	 * Draw the outer border. Default true. The SURFACE background is NOT optional either way — the edge
	 * fade masks itself against it — so a caller on a different ground should match that surface.
	 */
	bordered?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * Lets content wider than its container scroll sideways instead of overflowing it — without this, the
 * surplus is clipped by the nearest `overflow: hidden` ancestor and becomes unreachable.
 *
 * The edge fade is Lea Verou's `background-attachment: local, scroll` trick: the "cover" layers scroll
 * WITH the content and mask the edge "shadow" layers pinned to the box, so a fade shows only while there
 * is more to scroll that way. No JS, no scroll listeners.
 */
const scrollAreaSx = (bordered: boolean): SxProps<Theme> => ({
	overflowX: "auto",
	maxWidth: "100%",
	overscrollBehaviorX: "contain",
	...(bordered && {
		borderRadius: 1,
		border: `1px solid ${NEUTRAL[200]}`,
	}),
	backgroundColor: SURFACE,
	backgroundImage: [
		`linear-gradient(to right, ${SURFACE} 30%, transparent)`,
		`linear-gradient(to left, ${SURFACE} 30%, transparent)`,
		`radial-gradient(farthest-side at 0 50%, ${SHADOW.SCROLL_SHADOW_INK}, transparent)`,
		`radial-gradient(farthest-side at 100% 50%, ${SHADOW.SCROLL_SHADOW_INK}, transparent)`,
	].join(", "),
	backgroundRepeat: "no-repeat",
	backgroundSize: "40px 100%, 40px 100%, 16px 100%, 16px 100%",
	backgroundPosition: "0 0, 100% 0, 0 0, 100% 0",
	backgroundAttachment: "local, local, scroll, scroll",
});

export const HorizontalScrollArea: FC<Props> = ({ children, bordered = true, dataTestId, sx }) => (
	<Box data-testid={dataTestId} sx={[scrollAreaSx(bordered), ...(Array.isArray(sx) ? sx : [sx])]}>
		{children}
	</Box>
);
